import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Package,
    Truck,
    CheckCircle,
    Clock,
    MapPin,
    ChevronRight,
    AlertCircle,
    XCircle,
    RefreshCw,
    ExternalLink
} from 'lucide-react';
import NavbarMob from './NavbarMob';
import Navbar from './Navbar'; // Assuming you have this
import FooterMob from './FooterMob';
import Footer from './Footer';
import { useSelector } from 'react-redux';
import CryptoJS from "crypto-js";
import axios from 'axios';
import { useMediaQuery } from 'react-responsive';
import ModalImage from '../pages/modalImage';

function MyOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showOrdersSkeleton, setShowOrdersSkeleton] = useState(false);
    const [error, setError] = useState(null);
    const [productNames, setProductNames] = useState({});
    const [orderTrackingStatus, setOrderTrackingStatus] = useState({});
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [refreshingOrder, setRefreshingOrder] = useState(null);

    // console.log(orders, 'orders');


    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const apiLocalUrl = process.env.REACT_APP_API_LOCAL_URL;

    const userId = useSelector((state) => state.user.id);
    const LedCode = localStorage.getItem('LedCode');
    const navigate = useNavigate();

    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const isTab = useMediaQuery({ query: '(max-width: 1024px)' });

    // Fetch product name by Itemid
    const fetchProductName = async (itemId) => {
        try {
            const response = await fetch(
                `${apiBaseUrl}/getProductById/BLACKBATON_2526?Id=${itemId}`
            );
            if (!response.ok) return 'Unknown Product';
            const data = await response.json();
            return data.length > 0 ? data[0].ItemName : 'Unknown Product';
        } catch (error) {
            console.error('Error fetching product name:', error);
            return 'Unknown Product';
        }
    };

    // Fetch product details by UniqueCode
    const fetchProductDetails = async (uniqueCode) => {
        try {
            const response = await fetch(
                `${apiBaseUrl}/getProductByUniqueCode/BLACKBATON_2526?uniqueCode=${uniqueCode}`
            );
            if (!response.ok) return null;
            const data = await response.json();
            return data.length > 0 ? data[0] : null;
        } catch (error) {
            console.error('Error fetching product details:', error);
            return null;
        }
    };

    // Get latest tracking status
    const getLatestTrackingStatusWithTime = (trackingData) => {
        if (!trackingData?.ShipmentData?.[0]?.Shipment?.Scans) {
            return {
                status: 'Pending',
                timestamp: null,
                location: null,
                instructions: null
            };
        }

        const scans = trackingData.ShipmentData[0].Shipment.Scans;
        let latestScan = null;

        // Find the most recent valid scan
        for (let i = scans.length - 1; i >= 0; i--) {
            const scan = scans[i];
            if (scan.ScanDetail) {
                latestScan = scan.ScanDetail;
                break;
            }
        }

        if (!latestScan) {
            return {
                status: trackingData?.ShipmentData?.[0]?.Shipment?.Status?.Status || 'Pending',
                timestamp: null,
                location: null,
                instructions: null
            };
        }

        const status = checkAndFormatStatus(latestScan.Scan, latestScan.StatusCode);

        return {
            status,
            timestamp: latestScan.ScanDateTime || latestScan.StatusDateTime,
            location: latestScan.ScannedLocation,
            instructions: latestScan.Instructions,
            awb: trackingData.ShipmentData?.[0]?.Shipment?.AWB
        };
    };

    const checkAndFormatStatus = (scanText, statusCode) => {
        if (!scanText) return 'Pending';

        const text = scanText.toLowerCase();

        // Check cancellation statuses
        if (text === 'not picked' || statusCode === 'X-PNP' || statusCode === 'PNP') {
            return 'Order Cancelled';
        }

        if (text.includes('cancel')) {
            return 'Cancelled';
        }

        if (text.includes('rto')) {
            return 'RTO (Return to Origin)';
        }

        if (text.includes('undelivered')) {
            return 'Undelivered';
        }

        if (text.includes('rejected')) {
            return 'Rejected';
        }

        if (text.includes('delivered')) {
            return 'Delivered';
        }

        if (text.includes('out for delivery')) {
            return 'Out for Delivery';
        }

        if (text.includes('in transit')) {
            return 'In Transit';
        }

        if (text.includes('manifested')) {
            return 'Order Confirmed';
        }

        // Return the original scan text for other statuses
        return scanText;
    };

    // Fetch tracking status for an order
    const fetchOrderTrackingStatus = async (orderId, forceRefresh = false) => {
        if (refreshingOrder === orderId && !forceRefresh) return;

        try {
            if (forceRefresh) setRefreshingOrder(orderId);

            const res = await fetch(`${apiBaseUrl}/tracking-by-orderId/${orderId}`);
            const data = await res.json();

            if (res.ok && data.success && data.awb) {
                try {
                    const trackingRes = await axios.get(`${apiBaseUrl}/track-delhivery/${data.awb}`);
                    if (trackingRes.data.success) {
                        const statusInfo = getLatestTrackingStatusWithTime(trackingRes.data.data);

                        setOrderTrackingStatus(prev => ({
                            ...prev,
                            [orderId]: {
                                ...statusInfo,
                                awb: data.awb,
                                lastUpdated: new Date().toISOString()
                            }
                        }));
                    }
                } catch (err) {
                    console.error('Tracking fetch error:', err);
                    setOrderTrackingStatus(prev => ({
                        ...prev,
                        [orderId]: {
                            status: 'Tracking Error',
                            timestamp: null,
                            location: null,
                            instructions: null,
                            awb: data.awb,
                            lastUpdated: new Date().toISOString()
                        }
                    }));
                }
            } else {
                setOrderTrackingStatus(prev => ({
                    ...prev,
                    [orderId]: {
                        status: 'Not Trackable',
                        timestamp: null,
                        location: null,
                        instructions: null,
                        awb: null,
                        lastUpdated: new Date().toISOString()
                    }
                }));
            }
        } catch (err) {
            console.error('Order tracking error:', err);
            setOrderTrackingStatus(prev => ({
                ...prev,
                [orderId]: {
                    status: 'Error Fetching Status',
                    timestamp: null,
                    location: null,
                    instructions: null,
                    awb: null,
                    lastUpdated: new Date().toISOString()
                }
            }));
        } finally {
            if (forceRefresh) setRefreshingOrder(null);
        }
    };

    // Get status icon and color
    const getStatusConfig = (status) => {
        const lowerStatus = status.toLowerCase();

        if (lowerStatus.includes('delivered')) {
            return {
                icon: <CheckCircle className="w-5 h-5" />,
                bgColor: 'bg-gray-50',
                textColor: 'text-gray-800',
                borderColor: 'border-gray-200',
                badgeColor: 'bg-gray-100 text-gray-700'
            };
        }

        if (lowerStatus.includes('cancelled') || lowerStatus.includes('rejected') || lowerStatus.includes('rto')) {
            return {
                icon: <XCircle className="w-5 h-5" />,
                bgColor: 'bg-gray-50',
                textColor: 'text-gray-800',
                borderColor: 'border-gray-200',
                badgeColor: 'bg-gray-100 text-gray-700'
            };
        }

        if (lowerStatus.includes('out for delivery')) {
            return {
                icon: <Truck className="w-5 h-5" />,
                bgColor: 'bg-black',
                textColor: 'text-white',
                borderColor: 'border-black',
                badgeColor: 'bg-gray-100 text-gray-700'
            };
        }

        if (lowerStatus.includes('in transit')) {
            return {
                icon: <Truck className="w-5 h-5" />,
                bgColor: 'bg-gray-50',
                textColor: 'text-gray-800',
                borderColor: 'border-gray-200',
                badgeColor: 'bg-gray-100 text-gray-700'
            };
        }

        if (lowerStatus.includes('pending') || lowerStatus.includes('confirmed')) {
            return {
                icon: <Clock className="w-5 h-5" />,
                bgColor: 'bg-gray-50',
                textColor: 'text-gray-800',
                borderColor: 'border-gray-200',
                badgeColor: 'bg-gray-100 text-gray-700'
            };
        }

        // Default
        return {
            icon: <Package className="w-5 h-5" />,
            bgColor: 'bg-gray-50',
            textColor: 'text-gray-800',
            borderColor: 'border-gray-200',
            badgeColor: 'bg-gray-100 text-gray-700'
        };
    };

    // Format date
    const formatStamp = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };

    const formatOrderDate = (orderDate) => {
        if (!orderDate) return "N/A";

        try {
            // Extract YYYY-MM-DD safely (no timezone shift)
            const [year, month, day] = orderDate.split("T")[0].split("-");

            const date = new Date(year, month - 1, day);

            return date.toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric"
            });
        } catch {
            return orderDate;
        }
    };




    // Format time ago
    const formatTimeAgo = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMins / 60);
            const diffDays = Math.floor(diffHours / 24);

            if (diffDays > 0) return `${diffDays}d ago`;
            if (diffHours > 0) return `${diffHours}h ago`;
            if (diffMins > 0) return `${diffMins}m ago`;
            return 'Just now';
        } catch (e) {
            return '';
        }
    };

    const fetchProductSize = async (uniqueCode) => {
        try {
            const response = await fetch(
                `${apiBaseUrl}/getProductByUniqueCode/BLACKBATON_2526?uniqueCode=${uniqueCode}`
            );

            if (!response.ok) {
                throw new Error('Failed to fetch product size');
            }

            const data = await response.json();
            if (data.length > 0) {
                return data[0]; // Return the product size
            }
            return 'N/A'; // Default size if product not found
        } catch (error) {
            console.error('Error fetching product size:', error);
            return 'N/A'; // Default size if an error occurs
        }
    };


    const [modifiedColors, setModifiedColors] = useState([])

    const fetchModfiedColors = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/getAllModifiedColors/BLACKBATON_2526`);
            const data = await response.json();
            // console.log(data,'data');

            const modifiedColors = data.reduce((acc, item) => {
                acc[item.db_color] = {
                    name: item.md_color?.toUpperCase() || '',
                    hex: item.hex || ''
                };
                return acc;
            }, {});

            setModifiedColors(modifiedColors);
        } catch (error) {
            console.error('Error fetching color and size data:', error);
        }
    };

    // Fetch all orders
    useEffect(() => {
        const fetchOrders = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetch(
                    `${apiBaseUrl}/order/list/BLACKBATON_2526/${LedCode || userId}`
                );

                if (response.status === 404) {
                    setOrders([]);
                    setShowOrdersSkeleton(false);
                    setError(null);
                    return;
                }

                if (!response.ok) {
                    throw new Error('Failed to fetch orders');
                }

                const data = await response.json();
                if (!data.success) {
                    setOrders([]);
                    setShowOrdersSkeleton(false);
                    return;
                }

                const hasOrders = Array.isArray(data.orders) && data.orders.length > 0;
                setShowOrdersSkeleton(hasOrders);
                if (!hasOrders) {
                    setOrders([]);
                    return;
                }

                // Function to check if image exists
                const checkImageExists = async (image) => {
                    try {
                        const res = await fetch(`${apiLocalUrl}/check-image/${encodeURIComponent(image)}`);
                        if (!res.ok) {
                            return false;
                        }
                        const result = await res.json();
                        return result.exists;
                    } catch (err) {
                        return false;
                    }
                };



                // Function to get modified color name
                const getModifiedColor = async (color) => {
                    try {
                        const response = await fetch(`${apiBaseUrl}/getAllModifiedColors/BLACKBATON_2526`);
                        const data = await response.json();
                        const modifiedColors = data.reduce((acc, item) => {
                            acc[item.db_color] = item.md_color?.toUpperCase() || '';
                            return acc;
                        }, {});
                        return modifiedColors[color] || color;
                    } catch (error) {
                        return color;
                    }
                };

                // Group orders by EntryNo
                const groupedOrders = {};
                const productNamePromises = [];
                const imageCheckPromises = [];

                // First, process all orders to group them
                data.orders.forEach(order => {
                    if (!groupedOrders[order.EntryNo]) {
                        groupedOrders[order.EntryNo] = {
                            EntryNo: order.EntryNo,
                            LedCode: order.LedCode,
                            OrderDate: order.OrderDate,
                            OrderTime: order.OrderTime,
                            PhoneNo: order.PhoneNo,
                            items: [],
                            totalAmount: 0,
                            image: null // Will store the random image for this order
                        };
                    }

                    groupedOrders[order.EntryNo].items.push({
                        Itemid: order.Itemid,
                        UniqueCode: order.UniqueCode,
                        Quantity: order.Quantity,
                        SRate: order.SRate,
                        Total: order.Total,
                        mrp: order.mrp,
                        Size: null, // Will be populated later
                        Color: null, // Will be populated later
                        image: null // Will be populated later
                    });

                    groupedOrders[order.EntryNo].totalAmount += Number(order.Total) || 0;

                    // Collect product name promises
                    if (!productNames[order.Itemid]) {
                        productNamePromises.push(
                            fetchProductName(order.Itemid).then(name => {
                                productNames[order.Itemid] = name;
                            })
                        );
                    }
                });

                // Wait for all product names first
                await Promise.all(productNamePromises);

                // Now process each item to get size, color, and check images
                const ordersArray = Object.values(groupedOrders);

                for (const order of ordersArray) {
                    const imagePromisesForThisOrder = [];

                    for (const item of order.items) {
                        // Fetch product details for size and color
                        try {
                            const productDetails = await fetchProductDetails(item.UniqueCode);
                            if (productDetails) {
                                item.Size = productDetails.Size;
                                item.Color = productDetails.Color;

                                // Get modified color
                                const modifiedColor = await getModifiedColor(productDetails.Color);
                                // const modifiedColor = modifiedColors[productDetails.Color];

                                // Create image filename
                                const imageName = `${item.Itemid}_${modifiedColor}.webp`;

                                // console.log(imageName, 'imageName');


                                // Check if image exists and add to promises
                                // imagePromisesForThisOrder.push(
                                //     checkImageExists(imageName).then(exists => {
                                //         if (exists) {
                                //             item.image = `${apiLocalUrl}/uploads/${imageName}`;
                                //         }
                                //         return exists ? imageName : null;
                                //     })
                                // );

                                checkImageExists(imageName).then(exists => {
                                    if (exists) {
                                        item.image = `${apiLocalUrl}/uploads/${imageName}`;
                                    }
                                })

                            }
                        } catch (error) {
                            console.error('Error fetching product details:', error);
                        }
                    }

                    // Wait for all image checks for this order
                    const imageResults = await Promise.all(imagePromisesForThisOrder);

                    // Select a random image from existing ones for this order
                    const existingImages = imageResults.filter(img => img !== null);
                    if (existingImages.length > 0) {
                        // Select random image
                        const randomIndex = Math.floor(Math.random() * existingImages.length);
                        order.image = `${apiLocalUrl}/uploads/${existingImages[randomIndex]}`;
                    }
                }

                // Reverse to show latest orders first
                const sortedOrders = ordersArray.reverse();
                setOrders(sortedOrders);
                setProductNames({ ...productNames });

                // Fetch tracking status for each order
                sortedOrders.forEach(order => {
                    fetchOrderTrackingStatus(order.EntryNo);
                });

            } catch (error) {
                if (error?.message === 'Failed to fetch orders') {
                    setOrders([]);
                    setShowOrdersSkeleton(false);
                    setError(null);
                    return;
                }
                console.error('Error fetching orders:', error);
                setError(error.message);
            } finally {
                setLoading(false);
                setShowOrdersSkeleton(false);
            }
        };

        // fetchModfiedColors()
        fetchOrders();
    }, [apiBaseUrl, LedCode, userId]);

    // Navigation functions
    const homePage = () => navigate('/');
    const accountPage = () => navigate('/account');

    // Tracking functions
    const SECRET_KEY = "shersoft_blackbaton_delhivery";
    const encryptText = (text) => CryptoJS.AES.encrypt(text, SECRET_KEY).toString();

    const handleTrackOrder = (orderId) => {
        const encrypted = encodeURIComponent(encryptText(String(orderId)));
        navigate(`/delhiverytracking?i=${encrypted}`);
    };

    const handleRefreshStatus = (orderId, e) => {
        e.stopPropagation();
        fetchOrderTrackingStatus(orderId, true);
    };

    const [modalImage, setModalImage] = useState(false)
    const [mainImage, setMainImage] = useState(null);

    useEffect(() => {
        if (modalImage) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        // Clean up in case component unmounts
        return () => {
            document.body.style.overflow = 'auto';
        };

    }, [modalImage]);


    // Loading state
    if (loading && showOrdersSkeleton) {
        return (
            <div className="min-h-screen flex flex-col p-5 md:p-0">
                <div className='h-max block md:hidden'>
                    <NavbarMob />
                </div>
                <div className="flex-1 pt-16 md:p-0">
                    <div className="max-w-6xl mx-auto">
                        <div className="mb-8">
                            <div className="h-8 bg-gray-200 rounded w-48 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-64"></div>
                        </div>
                        {[1, 2, 3].map(i => (
                            <div key={i} className="mb-4 bg-gray-100 rounded-lg p-6 animate-pulse">
                                <div className="flex justify-between mb-4">
                                    <div className="h-6 bg-gray-300 rounded w-48"></div>
                                    <div className="h-6 bg-gray-300 rounded w-24"></div>
                                </div>
                                <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
                                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return null;
    }

    // Error state
    if (error) {
        return (
            <div className="max-h-screen flex flex-col p-5 md:p-0">
                <div className='h-max block md:hidden'>
                    <NavbarMob />
                </div>
                <div className="flex-1 flex items-center justify-center pt-16 md:p-0">
                    <div className="text-center max-w-md ">
                        <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">No Orders Found</h3>
                        <p className="text-gray-600 mb-6">You haven't placed any orders yet.</p>
                        <button
                            onClick={homePage}
                            className="px-6 py-3 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition duration-200"
                        >
                            Start Shopping
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">

            <div className="w-full h-full ">

                <div className='h-max block md:hidden'>
                    <NavbarMob />
                </div>

                {/* Main Content */}
                <div className="relative max-w-6xl h-full mx-auto pt-16 md:p-0 min-h-screen md:h-auto">
                    {/* Header */}
                    <div className="md:mb-8 text-left p-5 py-3 md:p-0 md:relative bg-white">
                        {/* Mobile Breadcrumbs */}
                        <div className='flex flex-row gap-2 items-center mb-4 md:hidden'>
                            <span className='text-xs font-[500] font-montserrat text-[#828282] cursor-pointer' onClick={homePage}>Home</span>
                            <span className='text-[#828282]'>{">"}</span>
                            <span className='text-xs font-[400] font-montserrat text-[#3C4242] cursor-pointer' onClick={accountPage}>Account</span>
                            <span className='text-[#828282]'>{">"}</span>
                            <span className='text-xs font-[400] font-montserrat text-[#3C4242]'>My Orders</span>
                        </div>
                        <h1 className="text-[1.1rem] md:text-3xl font-bold text-gray-900 mb-2">My Orders</h1>
                    </div>

                    {orders.length === 0 ? (
                        <div className="text-center py-16">
                            <Package className="w-20 h-20 text-gray-300 mx-auto mb-6" />
                            <h3 className="text-2xl font-bold text-gray-700 mb-3">No Orders Yet</h3>
                            <p className="text-gray-500 mb-8 max-w-md mx-auto">
                                You haven't placed any orders. Start shopping to see your orders here.
                            </p>
                            <button
                                onClick={homePage}
                                className="px-8 py-3 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition duration-200"
                            >
                                Explore Products
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-6  p-5 pt-0 md:p-0 w-full h-full">
                            {orders.map((order) => {
                                const trackingStatus = orderTrackingStatus[order.EntryNo];
                                const statusConfig = getStatusConfig(trackingStatus?.status || 'Pending');
                                const isExpanded = expandedOrder === order.EntryNo;

                                return (
                                    <div
                                        key={order.EntryNo}
                                        className={`overflow-auto bg-white rounded-xl border font-montserrat ${statusConfig.borderColor} transition-all duration-300 hover:shadow-lg`}
                                    >
                                        <div className="p-3 md:p-5 h-full flex flex-row justify-between gap-4">
                                            <div className="flex flex-row gap-3">
                                                <div className='' onClick={() => {
                                                    setModalImage(true)
                                                    setMainImage(order.items[0].image)
                                                }}>
                                                    <img className='max-w-[5rem] h-full max-h-[5rem] rounded-md' src={order.items[0].image} alt="ad" />
                                                    {/* {statusConfig.icon} */}
                                                </div>

                                                <div className="flex flex-col items-start gap-2">
                                                    <div className='flex flex-col gap-1'>
                                                        <div className="flex items-center gap-3">
                                                            <h3 className="text-[1rem] font-bold text-gray-900">
                                                                #{order.EntryNo}
                                                            </h3>
                                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig.badgeColor}`}>
                                                                {trackingStatus?.status || 'Checking Status...'}
                                                            </span>
                                                        </div>
                                                        <p className="text-sm text-gray-600 text-left">
                                                            Placed on {formatOrderDate(order.OrderDate)}
                                                        </p>
                                                        {trackingStatus?.instructions && (
                                                            <p className="text-sm text-gray-600 text-left">
                                                                {trackingStatus.instructions}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {trackingStatus?.location && (
                                                        <div className="flex items-start text-sm text-left gap-2">
                                                            {/* <span>•</span> */}
                                                            <MapPin className="w-4 h-4 mt-[.1rem]" />
                                                            <span className='break-words text-left'>{trackingStatus.location}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className='min-h-full flex flex-col items-end justify-between  gap-4 font-montserrat'>
                                                <div className="flex flex-col md:flex-row text-right items-center text-sm gap-2">
                                                    <Package className="w-4 h-4 hidden md:block" />
                                                    <span className='text-right'>
                                                        {order.items.reduce((total, item) => total + (Number(item.Quantity) || 0), 0)}&nbsp;
                                                        item{order.items.reduce((total, item) => total + (Number(item.Quantity) || 0), 0) !== 1 ? 's' : ''}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <span>•</span>
                                                        <span className="font-medium text-gray-900">₹{order.totalAmount.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                                {trackingStatus?.status !== 'Not Trackable' && (
                                                    <div className="">

                                                        {trackingStatus?.status?.toLowerCase().includes('delivered') ? (
                                                            <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg cursor-not-allowed">
                                                                <CheckCircle className="w-4 h-4" />
                                                                <span>Delivered</span>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleTrackOrder(order.EntryNo);
                                                                }}
                                                                className="flex items-center text-sm gap-2 px-4 p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"

                                                            >
                                                                <span className='hidden md:block'>Track Order</span>
                                                                <span className='block md:hidden'>Track</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>


                                    </div>
                                );
                            })}
                        </div>
                    )}


                </div>

                <div className='h-max block md:hidden'>
                    <FooterMob />

                </div>

            </div>

            {modalImage && (
                <div className="fixed top-0 left-0 w-screen h-screen flex justify-center items-center bg-black bg-opacity-50 z-50">
                    <ModalImage imageUrl={mainImage} onClose={() => setModalImage(false)} />
                </div>
            )}
        </div>
    );
}

export default MyOrders;
